
import Image from "./Image";
import { Paperclip, Send, Link2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
/* Slim phone screen footprint. Sized to sit comfortably inside the capability card without
   feeling too wide — a tall, narrow handset proportion. */
const SCREEN = "relative aspect-[9/17] w-full overflow-hidden rounded-t-xl rounded-b-[1.5rem] bg-[#20242c]";
/* 16 unique renters fill a 4×4 grid, all full color. The rideshare (Uber) driver — Jordan —
   is the one the cursor selects and that merges into the chat header avatar. */
const GUESTS = [
    { src: "/images/guests/leisure.webp", alt: "Leisure traveler" },
    { src: "/images/guests/delivery.webp", alt: "Delivery driver" },
    { src: "/images/guests/packages.webp", alt: "Package courier" },
    { src: "/images/guests/corporate.webp", alt: "Business traveler" },
    { src: "/images/guests/student.webp", alt: "Student renter" },
    { src: "/images/guests/rideshare.webp", alt: "Rideshare driver", merge: true },
    { src: "/images/guests/guest-1.webp", alt: "Renter" },
    { src: "/images/guests/guest-2.webp", alt: "Renter" },
    { src: "/images/guests/guest-3.webp", alt: "Renter" },
    { src: "/images/guests/guest-4.webp", alt: "Renter" },
    { src: "/images/guests/guest-5.webp", alt: "Renter" },
    { src: "/images/guests/guest-6.webp", alt: "Renter" },
    { src: "/images/guests/guest-7.webp", alt: "Renter" },
    { src: "/images/guests/guest-8.webp", alt: "Renter" },
    { src: "/images/guests/guest-9.webp", alt: "Renter" },
    { src: "/images/guests/guest-10.webp", alt: "Renter" },
];
/* An earlier thread that is already on screen when the conversation opens — it establishes
   the returning-renter relationship (Jordan just returned the Camry). "renter" = Jordan
   (left, gray); "host" = the operator (right, green). */
const HISTORY = [
    { from: "date", text: "Yesterday · 2:14 PM" },
    { from: "renter", text: "I dropped off the Camry — it was perfect. Can I book it for next week?" },
    { from: "host", text: "Thanks Jordan! And sure thing." },
];
/* The new exchange that animates in. Renter messages are preceded by a "typing…" dots bubble;
   host messages are preceded by the text being typed into the input container. The host's
   agreement link renders as a shortened-URL chip. */
const SCRIPT = [
    { from: "date", text: "Today · 10:32 AM" },
    { from: "host", text: "Hey Jordan, did you want the Camry again?" },
    { from: "renter", text: "Yes I do" },
    { from: "host", text: "Want me to reserve it, or will you book online?" },
    { from: "renter", text: "Reserve for me — and can you send me the contract?" },
    { from: "host", text: "Yes! Here's your rental agreement:" },
    { from: "host", text: "esign.link/r/CAMRY-7F2", link: true },
    { from: "renter", text: "Ok, signed. Thank you!" },
    { from: "host", text: "Have a safe trip!" },
];
export function CrmChatPreview() {
    const [phase, setPhase] = useState("avatars");
    const [hover, setHover] = useState(false);
    const [cursor, setCursor] = useState({
        x: 0,
        y: 0,
        visible: false,
    });
    const [flyTransform, setFlyTransform] = useState("none");
    const [messages, setMessages] = useState([]);
    const [typing, setTyping] = useState(null);
    const [draft, setDraft] = useState("");
    const [instant, setInstant] = useState(false);
    /* CRM plays when it scrolls into view (no longer hover-gated) and loops from there. */
    const [playing, setPlaying] = useState(false);
    const frameRef = useRef(null);
    const jordanRef = useRef(null);
    const headerAvatarRef = useRef(null);
    /* Pointer hover over the card — when true, the opening avatar hold is cut short so the cursor
       animation kicks off sooner. Kept in a ref so hovering mid-hold takes effect without a rerun. */
    const hoverRef = useRef(false);
    useEffect(() => {
        const el = frameRef.current;
        if (!el)
            return;
        const observer = new IntersectionObserver(([entry]) => setPlaying(entry.isIntersecting), { threshold: 0.35 });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    useEffect(() => {
        // Per-run cancellation flag. Kept local to this effect run so that when `playing` toggles
        // (e.g. scroll jitter re-firing the observer) the OLD run() loop is truly cancelled instead
        // of being un-cancelled by a shared ref — which previously let multiple loops play at once,
        // duplicating messages and replaying the cursor/merge sequence.
        let cancelled = false;
        const reduce = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduce) {
            setPhase("chat");
            setMessages([...HISTORY, ...SCRIPT]);
            return;
        }
        if (!playing) {
            // Hold on the start frame — the full avatar grid — until the card becomes active.
            setInstant(true);
            setPhase("avatars");
            setHover(false);
            setCursor({ x: 0, y: 0, visible: false });
            setFlyTransform("none");
            setMessages([]);
            setTyping(null);
            setDraft("");
            return;
        }
        /* PACE slows the whole scripted thread back to its original, unhurried cadence — every
           beat (typing, message reveals, pauses) is scaled by this single factor. */
        const PACE = 1.3;
        const sleep = (ms) => new Promise((res) => setTimeout(res, ms * PACE));
        const stop = () => cancelled;
        const typeDraft = async (text) => {
            for (let i = 1; i <= text.length; i++) {
                if (stop())
                    return;
                setDraft(text.slice(0, i));
                await sleep(28);
            }
        };
        /* Measure Jordan's grid cell against the (laid-out but invisible) header avatar to
           compute the fly-to-header transform, plus the cursor start/target points. */
        const measure = () => {
            const f = frameRef.current;
            const j = jordanRef.current;
            const h = headerAvatarRef.current;
            if (!f || !j || !h)
                return { start: { x: 0, y: 0 }, target: { x: 0, y: 0 } };
            const fr = f.getBoundingClientRect();
            const jr = j.getBoundingClientRect();
            const hr = h.getBoundingClientRect();
            const dx = hr.left + hr.width / 2 - (jr.left + jr.width / 2);
            const dy = hr.top + hr.height / 2 - (jr.top + jr.height / 2);
            const scale = hr.width / jr.width;
            setFlyTransform(`translate(${dx}px, ${dy}px) scale(${scale})`);
            return {
                start: { x: fr.width * 0.82, y: fr.height * 0.9 },
                target: { x: jr.left + jr.width / 2 - fr.left, y: jr.top + jr.height / 2 - fr.top },
            };
        };
        const run = async () => {
            while (!stop()) {
                // Reset — snap everything back instantly (no residual slide on loop restart).
                setInstant(true);
                setPhase("avatars");
                setHover(false);
                setCursor({ x: 0, y: 0, visible: false });
                setFlyTransform("none");
                setMessages([]);
                setTyping(null);
                setDraft("");
                await sleep(60);
                if (stop())
                    return;
                setInstant(false);
                // All faces are already on screen from the start — hold on the people icons before the
                // cursor enters. Normally ~4.5s, but if the card is hovered the hold is cut to a brief
                // beat so the cursor animation starts sooner. Polled in small steps so a hover part-way
                // through the hold still shortens it.
                {
                    const HOLD_MS = 4500;
                    const HOVER_HOLD_MS = 500;
                    const STEP_MS = 120;
                    let waited = 0;
                    while (waited < HOLD_MS && !stop()) {
                        if (hoverRef.current && waited >= HOVER_HOLD_MS)
                            break;
                        await new Promise((res) => setTimeout(res, STEP_MS));
                        waited += STEP_MS;
                    }
                }
                if (stop())
                    return;
                // Cursor enters bottom-right, then travels to Jordan.
                if (stop())
                    return;
                const { start, target } = measure();
                setCursor({ ...start, visible: true });
                await sleep(60);
                if (stop())
                    return;
                setCursor({ ...target, visible: true });
                await sleep(820);
                // Cursor has arrived — green hover ring appears around Jordan (not before).
                if (stop())
                    return;
                setHover(true);
                await sleep(620);
                // Jordan flies up to the header; the cursor fades out.
                if (stop())
                    return;
                setPhase("merge");
                setCursor((c) => ({ ...c, visible: false }));
                await sleep(300);
                // Chat crossfades in while Jordan is still traveling, so the header slot is
                // already there to catch him as he docks.
                if (stop())
                    return;
                setPhase("chat");
                // The earlier thread is already on screen when the conversation opens.
                setMessages(HISTORY);
                await sleep(1100);
                // A date separator is held back and revealed together with the next message, so the
                // "Today" stamp lands exactly when that text reaches Jordan.
                let pendingDate = null;
                const deliver = (turn) => {
                    const batch = pendingDate ? [pendingDate, turn] : [turn];
                    pendingDate = null;
                    setMessages((prev) => [...prev, ...batch]);
                };
                for (const turn of SCRIPT) {
                    if (stop())
                        return;
                    if (turn.from === "date") {
                        pendingDate = turn;
                    }
                    else if (turn.from === "renter") {
                        setTyping("renter");
                        await sleep(1100);
                        if (stop())
                            return;
                        setTyping(null);
                        deliver(turn);
                        await sleep(750);
                    }
                    else {
                        await typeDraft(turn.text);
                        await sleep(400);
                        if (stop())
                            return;
                        setDraft("");
                        deliver(turn);
                        await sleep(850);
                    }
                }
                await sleep(2600);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [playing]);
    const mergeAvatar = GUESTS.find((g) => g.merge) ?? GUESTS[0];
    return (<div ref={frameRef} onPointerEnter={() => {
            hoverRef.current = true;
        }} onPointerLeave={() => {
            hoverRef.current = false;
        }} className="relative mx-auto w-full max-w-[236px]" role="img" aria-label="Renters from across the platform fill a grid; a cursor selects the rideshare driver and opens a live messenger conversation with the host to send and sign a rental agreement">
      {/* ── Chat layer — the phone mockup. In normal flow so it drives the frame height;
            the avatar grid and cursor overlay on top of it during the selection phase. */}
      <div className="mx-auto w-full max-w-[190px] transition-opacity duration-300" style={{ opacity: phase === "chat" ? 1 : 0 }}>
        {/* Phone shell — dark bezel with a top speaker pill; the chat screen is clipped inside. */}
        <div className="relative rounded-[2rem] border border-white/[0.12] bg-gradient-to-b from-[#15181f] to-[#0b0d11] px-2 pb-2 pt-4 shadow-[0_30px_70px_-20px_rgba(0,0,0,0.8)] ring-1 ring-black/50">
          <span aria-hidden="true" className="absolute left-1/2 top-2 h-1 w-10 -translate-x-1/2 rounded-full bg-white/15"/>
          <div className={SCREEN}>
            <div className="absolute inset-0 flex flex-col">
              {/* Header — merged renter avatar sits top-left. */}
              <div className="flex items-center gap-2 border-b border-white/10 bg-[#2b313c]/80 px-3 py-2">
                <span ref={headerAvatarRef} className="relative h-7 w-7 overflow-hidden rounded-full ring-1 ring-white/15">
                  <Image src={mergeAvatar.src || "/placeholder.svg"} alt="" fill priority sizes="28px" className="object-cover"/>
                </span>
                <div className="min-w-0">
                  <p className="font-sans text-[11px] font-semibold text-white">Jordan Miller</p>
                  <p className="font-sans text-[9px] text-[#8affdf]">Active now</p>
                </div>
              </div>

              {/* Thread. min-h-0 lets it shrink so a growing stack clips at the top instead of
            pushing the input bar out the bottom of the frame. */}
              <div className="flex min-h-0 flex-1 flex-col justify-end gap-1.5 overflow-hidden px-3 pb-2 pt-1.5">
                {messages.map((m, i) => m.from === "date" ? (<div key={i} className="crm-msg-in flex justify-center py-1">
                      <span className="font-sans text-[8px] font-medium tracking-wide text-white/45">
                        {m.text}
                      </span>
                    </div>) : (<div key={i} className={`crm-msg-in flex ${m.from === "host" ? "justify-end" : "justify-start"}`}>
                    <span className={`max-w-[80%] rounded-2xl px-2.5 py-1.5 font-sans text-[10px] leading-snug ${m.from === "host"
                ? "rounded-br-sm bg-[#33F7BE] text-[#052b22]"
                : "rounded-bl-sm bg-[#2b313c] text-white/85"} ${m.link ? "inline-flex items-center gap-1 font-medium underline decoration-[#052b22]/40 underline-offset-2" : ""}`}>
                      {m.link && <Link2 aria-hidden="true" className="h-3 w-3 shrink-0"/>}
                      {m.text}
                    </span>
                  </div>))}

                {/* Renter typing indicator. */}
                {typing === "renter" && (<div className="crm-msg-in flex justify-start">
                    <span className="inline-flex items-center gap-1 rounded-2xl rounded-bl-sm bg-[#2b313c] px-3 py-2">
                      {[0, 1, 2].map((d) => (<span key={d} className="crm-typing-dot h-1.5 w-1.5 rounded-full bg-white/60" style={{ animationDelay: `${d * 0.18}s` }}/>))}
                    </span>
                  </div>)}
              </div>

              {/* Input bar — attach (left), type container (middle), send (right). */}
              <div className="flex items-center gap-1.5 border-t border-white/10 bg-[#2b313c]/60 px-2.5 py-2">
                <button type="button" tabIndex={-1} aria-hidden="true" className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white/45">
                  <Paperclip className="h-3.5 w-3.5"/>
                </button>
                <div className="flex min-w-0 flex-1 items-center rounded-full bg-[#20242c] px-3 py-1.5">
                  {draft ? (<span className="truncate font-sans text-[10px] text-white/85">
                      {draft}
                      <span className="crm-caret ml-px inline-block h-3 w-px align-middle bg-[#33F7BE]"/>
                    </span>) : (<span className="font-sans text-[10px] text-white/35">Message…</span>)}
                </div>
                <button type="button" tabIndex={-1} aria-hidden="true" className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors ${draft ? "bg-[#33F7BE] text-[#052b22]" : "bg-white/10 text-white/40"}`}>
                  <Send className="h-3 w-3"/>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Avatar grid layer (selection + merge) — overlays the phone during selection ── */}
      <div className="absolute inset-0 flex items-center justify-center transition-opacity duration-300" style={{ opacity: phase === "chat" ? 0 : 1 }} aria-hidden="true">
        <div className="grid grid-cols-4 gap-2">
          {GUESTS.map((g) => {
            const flying = (phase === "merge" || phase === "chat") && g.merge;
            const faded = (phase === "merge" || phase === "chat") && !g.merge;
            const transform = flying ? flyTransform : "none";
            const opacity = faded ? 0 : 1;
            return (<span key={g.src} ref={g.merge ? jordanRef : undefined} className="relative h-11 w-11 rounded-full" style={{
                    zIndex: g.merge ? 20 : 1,
                    opacity,
                    transform,
                    transition: instant
                        ? "none"
                        : "opacity 320ms ease, transform 700ms cubic-bezier(0.4,0,0.2,1)",
                }}>
                {/* Green selection ring — fades in only once the cursor arrives. */}
                {g.merge && (<span className="pointer-events-none absolute -inset-1 rounded-full border-2 border-[#33F7BE] shadow-[0_0_14px_rgba(51,247,190,0.55)]" style={{
                        opacity: hover ? 1 : 0,
                        transform: hover ? "scale(1)" : "scale(0.8)",
                        transition: instant ? "none" : "opacity 200ms ease, transform 260ms cubic-bezier(0.34,1.56,0.64,1)",
                    }}/>)}
                <span className="absolute inset-0 overflow-hidden rounded-full ring-1 ring-white/10 shadow-[0_6px_16px_rgba(0,0,0,0.45)]">
                  <Image src={g.src || "/placeholder.svg"} alt="" fill priority sizes="44px" className="object-cover"/>
                </span>
              </span>);
        })}
        </div>
      </div>

      {/* Mouse cursor. */}
      <div className="pointer-events-none absolute left-0 top-0 z-30" style={{
            transform: `translate(${cursor.x}px, ${cursor.y}px)`,
            opacity: cursor.visible ? 1 : 0,
            transition: instant
                ? "none"
                : "transform 820ms cubic-bezier(0.4,0,0.2,1), opacity 240ms ease",
        }} aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]">
          <path d="M3 2L15.5 10.5L10 11L13 16.5L10.5 18L7.5 12.5L3 15.5V2Z" fill="white" stroke="#0b0d12" strokeWidth="1" strokeLinejoin="round"/>
        </svg>
      </div>
    </div>);
}
